import {
  Competition,
  Cycle,
  CycleWithRels,
  GameWithRels,
  Gender,
  KeysToCamelCase,
  Mapping,
  MetricDistributionPercentiles,
  MetricDistributionBucket,
  Season,
  Team,
  TeamNavigationLinkIds,
  TeamWithRels,
} from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithQuery, atomWithSuspenseQuery } from 'jotai-tanstack-query';
import localforage from 'localforage';
import { fetchClientAtom } from '@/atoms/queries/client';
import { metricDistributionPercentilesUrl, metricDistributionBucketsUrl } from '@/query/url';
import { isSharingId, sortAscArrayOptions } from '@/utils/array';
import { createCompetitionOptionsAtom, createSeasonOptionsAtom } from '@/utils/atoms/cycles';
import { createOptionsAtom } from '@/utils/atoms/generic';
import { sortCyclesBySeasonAndComp } from '@/utils/cycles';
import { filterCyclesWithRelsByCompetition, filterCyclesBy } from '@/utils/filters';
import { datahubToIqIdMapping } from '@/utils/idMapping';
import { formatMetricDistributionBuckets, formatMetricDistributions } from '@/utils/metrics';
import { convertToDropdownOption } from '@/utils/object';
import { buildOptionSearchIndexAsync } from '@/utils/optionSearch';
import { objSnakeToCamel } from '@/utils/queries';
import { competitionIdsToFilterByAtom } from '../filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '../filters/highLevel/seasons';
import { metricsAtom } from '../metric';
import { globalTeamIdAtom, teamIdAtom } from '../team/team';
import { allTeamsAtom } from './allTeams';
import { competitionLookupAtom } from './competitions';
import { rawCyclesAtom } from './cycles';
import { seasonLookupAtom } from './seasons';
import { settingsConfigAtom, unwrappedGenderAtom } from './userConfigs';

/**
 * This file has become very large to avoid circular dependencies with other files.
 * We should split some bits of this file out, but care needs to be taken that we don’t end up with circular dependencies when we do this.
 * Consider doing this if you're making changes in this file.
 * Some ideas:
 * rawTeamNavigationLinkIdsAtom and the things derived from it could be in a separate file.
 * rawTeamIdMappingAtom and its derived atom could be in a separate file
 * userDefaultTeamIdsAtom and atoms derived from it could be in a separate file.
 */

const rawTeamsPath = (gender: Gender | undefined) => `/teams?eq[gender]=${gender}`;

const rawTeamsAtom = atomWithQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const gender = get(unwrappedGenderAtom);
  const queryKey = ['teams', gender] as const;

  const queryFn = async ({ queryKey: [, gender] }: { queryKey: typeof queryKey }) => {
    const teamsResponse = (await fetch(rawTeamsPath(gender))) as TeamWithRels[];
    // don't await this, we don't need to wait for it to finish
    localforage.setItem(rawTeamsPath(gender), teamsResponse);
    return teamsResponse;
  };

  return { queryKey, queryFn, enabled: !!gender };
});

// Returns either the result of the rawTeamsAtom, or if that is pending falls back to the localforage cache.
export const unwrappedRawTeamsAtom = atom<Promise<TeamWithRels[]>>(async get => {
  const { data, isPending } = get(rawTeamsAtom);

  if (isPending) {
    const gender = get(unwrappedGenderAtom);
    const localForageTeams = await localforage.getItem<TeamWithRels[]>(rawTeamsPath(gender));
    return localForageTeams || Promise.resolve([]);
  }

  return data ? Promise.resolve(data) : Promise.resolve([]);
});

export const isPopulatingTeamCacheAtom = atom(
  async get => get(rawTeamsAtom).isPending && (await get(unwrappedRawTeamsAtom)).length === 0,
);

// Same selection as the radar bio table's default cycle: sortCyclesBySeasonAndComp puts the most
// recent domestic league first, then the most recent of the remaining competitions.
export const getTeamPrimaryCompetition = (teamCycles: KeysToCamelCase<CycleWithRels>[]): string | null =>
  sortCyclesBySeasonAndComp(teamCycles)[0]?.competition.name ?? null;

// Join flat cycles with their competition and season into camelCased CycleWithRels, keyed by cycle id.
// Shared by allTeamsOptionsAtom (dropdown context) and the global search index so the two can't diverge.
export const buildCycleWithRelsLookup = (
  cycles: Cycle[],
  competitionLookup: Map<number, Competition>,
  seasonLookup: Map<number, Season>,
): Map<number, KeysToCamelCase<CycleWithRels>> =>
  new Map(
    cycles.flatMap(cycle => {
      const competition = competitionLookup.get(cycle.competition_id);
      const season = seasonLookup.get(cycle.season_id);
      return competition && season ? [[cycle.cycle_id, objSnakeToCamel({ cycle, competition, season })]] : [];
    }),
  );

export const getTeamCompetitionName = (
  teamCycleIds: number[],
  cycleWithRelsById: Map<number, KeysToCamelCase<CycleWithRels>>,
): string | null =>
  getTeamPrimaryCompetition(
    teamCycleIds.flatMap(id => {
      const cycleWithRels = cycleWithRelsById.get(id);
      return cycleWithRels ? [cycleWithRels] : [];
    }),
  );

export const allTeamsOptionsAtom = atom(async get => {
  const [teams, rawCycles, competitionLookup, seasonLookup] = await Promise.all([
    get(unwrappedRawTeamsAtom),
    get(rawCyclesAtom),
    get(competitionLookupAtom),
    get(seasonLookupAtom),
  ]);

  const cycleWithRelsById = buildCycleWithRelsLookup(rawCycles.data, competitionLookup, seasonLookup);

  return sortAscArrayOptions(
    teams.map(team => {
      const primaryCompetition = getTeamCompetitionName(team.cycle_ids, cycleWithRelsById);
      return convertToDropdownOption(team.name, team.team_id, {
        countryCode: team.area_code,
        context: primaryCompetition ? [primaryCompetition] : [],
      });
    }),
  );
});

export const allTeamsSearchIndexAtom = atom(async get => buildOptionSearchIndexAsync(await get(allTeamsOptionsAtom)));

export const unwrappedTeamsSearchIndexAtom = unwrap(allTeamsSearchIndexAtom);

export const teamsAtom = atom(async get => {
  const rawTeams = await get(unwrappedRawTeamsAtom);
  const rawCycles = (await get(rawCyclesAtom)).data;
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);
  const selectedSeasonsIds = get(seasonIdsToFilterByAtom);

  // filter the cycles by selected competitions and seasons to create an array of "valid" cycles
  const filteredCyclesByCompetition = filterCyclesBy(rawCycles, selectedCompetitionIds, 'competition_id');
  const filteredCyclesBySeasons = filterCyclesBy(filteredCyclesByCompetition, selectedSeasonsIds, 'season_id');
  const validCycleIds = filteredCyclesBySeasons.map(cycle => cycle.cycle_id);

  // filter available teams by comparing the cycles they played in and the valid cycles ids
  const filteredTeamsByCycles =
    selectedCompetitionIds.length || selectedSeasonsIds.length
      ? rawTeams.filter(team => isSharingId(team.cycle_ids, validCycleIds))
      : rawTeams;

  return filteredTeamsByCycles;
});

export const teamsOptionsAtom = createOptionsAtom(teamsAtom, 'name', 'team_id', ['area_code']);

const rawTeamCyclesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamCycles', get(teamIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, teamId] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/team/${teamId}/cycles`)) as Promise<CycleWithRels[]>;
  return { queryKey, queryFn };
});

export const teamCyclesAtom = atom(async get => {
  const rawTeamCycles = await get(rawTeamCyclesAtom);
  return rawTeamCycles.data.map(objSnakeToCamel);
});

export const teamCyclesOrderedAtom = atom(async get => {
  const teamCycles = await get(teamCyclesAtom);
  return sortCyclesBySeasonAndComp(teamCycles);
});

const filteredTeamCyclesSeasonAtom = atom(async get => {
  const teamCycles = await get(teamCyclesAtom);
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);

  return filterCyclesWithRelsByCompetition(teamCycles, selectedCompetitionIds);
});

export const teamCompetitionOptionsAtom = createCompetitionOptionsAtom(teamCyclesAtom);

export const teamSeasonOptionsAtom = createSeasonOptionsAtom(filteredTeamCyclesSeasonAtom);

const rawTeamGamesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamGames', get(teamIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, teamId] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/team/${teamId}/games`)) as Promise<GameWithRels[]>;

  return { queryKey, queryFn };
});

export const teamGamesAtom = atom(async get => {
  const teamGames = await get(rawTeamGamesAtom);
  const selectedTeamId = get(teamIdAtom);

  const formattedTeamGames = teamGames.data.map(objSnakeToCamel).map(game => ({
    ...game,
    team: { teamId: selectedTeamId },
  }));

  return formattedTeamGames;
});

export const unwrappedTeamGamesAtom = unwrap(teamGamesAtom, prev => prev ?? []);

const rawSelectedTeamInfoAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['selectedTeamInfo', get(teamIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, teamId] }: { queryKey: typeof queryKey }) => {
    if (!teamId) return [];

    return (await fetch(`/teams?eq[team_id]=${teamId}`)) as Promise<Team[]>;
  };

  return { queryKey, queryFn };
});

export const selectedTeamInfoAtom = atom(async get => (await get(rawSelectedTeamInfoAtom)).data[0]);

const rawTeamIdMappingAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamIdMapping'] as const;

  const queryFn = async () => (await fetch('/teams/mapping')) as Promise<Mapping[]>;
  return { queryKey, queryFn };
});

export const teamIdMappingAtom = atom(async get => datahubToIqIdMapping((await get(rawTeamIdMappingAtom)).data));

export const unwrappedTeamIdMappingAtom = unwrap(teamIdMappingAtom, prev => prev || {});

export const userDefaultTeamIdsAtom = atom(async get => {
  const [settingsConfig, allTeams] = await Promise.all([get(settingsConfigAtom), get(allTeamsAtom)]);
  if (settingsConfig) {
    const { datahubId, iqId } = settingsConfig.definition.team;
    // we need check if the id is valid (i.e. they have the licence for it)
    const isValidPersonalDefaultTeamId = datahubId && allTeams.some(team => team.team_id === datahubId);
    if (isValidPersonalDefaultTeamId) return { datahubId, iqId };
  }

  // returning undefined will trigger the default team prompt modal
  return undefined;
});

const unwrappedUserDefaultTeamIdsAtom = unwrap(userDefaultTeamIdsAtom, prev => prev || undefined);

const rawUserDefaultTeamCyclesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['userDefaultTeamCycles', get(unwrappedUserDefaultTeamIdsAtom)?.datahubId] as const;

  const queryFn = async ({ queryKey: [_, teamId] }: { queryKey: typeof queryKey }) => {
    if (!teamId) return [];

    return (await fetch(`/team/${teamId}/cycles`)) as Promise<CycleWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const userDefaultTeamCyclesOrderedAtom = atom(async get => {
  const rawUserDefaultTeamCycles = await get(rawUserDefaultTeamCyclesAtom);
  const userDefaultTeamCycles = rawUserDefaultTeamCycles.data.map(objSnakeToCamel);

  return sortCyclesBySeasonAndComp(userDefaultTeamCycles);
});

export const unwrappedUserDefaultTeamCyclesOrderedAtom = unwrap(userDefaultTeamCyclesOrderedAtom, prev => prev || null);

const rawTeamNavigationLinkIdsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const globalTeamId = get(globalTeamIdAtom);
  const queryKey = ['teamNavigationLinkIds', globalTeamId] as const;

  const queryFn = async ({ queryKey: [_, globalTeamId] }: { queryKey: typeof queryKey }) => {
    const noResultResponse = {
      /* this is following a pattern to just have the links use 0 id */
      player: { datahub_id: 0, iq_id: 0 },
      game: { datahub_id: 0, iq_id: 0 },
      competition: { datahub_id: 0, iq_id: 0 },
      keeper: { datahub_id: 0, iq_id: 0 },
    };

    // If a team is in context, fetch its navigation links. Using globalTeamId alone (not
    // hasUserSelectedTeam) matches the pattern in navigationTeamIdAtom and avoids a race
    // where hasUserSelectedTeam lags globalTeamId by one render, causing all four nav link
    // types (keeper, game, competition, player) to resolve to the wrong team.
    if (globalTeamId) {
      try {
        const result = await fetch(`/team/${globalTeamId}/navigation-link-ids`);
        // fetch is typed any; cast relies on the API contract for /team/{id}/navigation-link-ids
        return result as TeamNavigationLinkIds;
      } catch (err: unknown) {
        // Log an error if we failed to fetch the specific team links - try the default as a backup
        // eslint-disable-next-line no-console
        console.warn(`Failed to fetch navigation links for globalTeamId ${globalTeamId}:`, err);
      }
    }

    // Either no team is in context, or the fetch above failed — fall back to default team links.
    // This means the nav bar still renders with valid links rather than broken zero-ID links.
    const defaultTeamId = (await get(userDefaultTeamIdsAtom))?.datahubId;

    if (!defaultTeamId) return noResultResponse;

    try {
      const result = await fetch(`/team/${defaultTeamId}/navigation-link-ids`);
      // fetch is typed any; cast relies on the API contract for /team/{id}/navigation-link-ids
      return result as TeamNavigationLinkIds;
    } catch (err: unknown) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to fetch navigation links for defaultTeamId ${defaultTeamId}:`, err);
      return noResultResponse;
    }
  };

  return { queryKey, queryFn };
});

const teamNavigationLinkIdsAtom = atom<Promise<TeamNavigationLinkIds>>(
  async get => (await get(rawTeamNavigationLinkIdsAtom)).data,
);

export const teamNavigationPlayerLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(teamNavigationLinkIdsAtom)).player),
);

export const teamNavigationGameLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(teamNavigationLinkIdsAtom)).game),
);

export const teamNavigationCompetitionLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(teamNavigationLinkIdsAtom)).competition),
);

export const teamNavigationKeeperLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(teamNavigationLinkIdsAtom)).keeper),
);

const rawDefaultTeamInfoAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['defaultTeamInfo', get(unwrappedUserDefaultTeamIdsAtom)?.datahubId] as const;

  const queryFn = async ({ queryKey: [_, teamId] }: { queryKey: typeof queryKey }) => {
    if (!teamId) return null;

    return (await fetch(`/teams?eq[team_id]=${teamId}`)) as Promise<Team[]>;
  };

  return { queryKey, queryFn };
});

export const defaultTeamInfoAtom = atom(async get => (await get(rawDefaultTeamInfoAtom))?.data?.[0]);

const rawTeamMetricDistributionPercentilesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamMetricDistributionPercentiles', get(unwrappedGenderAtom), get(metricsAtom)] as const;
  const queryFn = async ({ queryKey: [, gender, metrics] }: { queryKey: typeof queryKey }) => {
    if (!gender || !metrics.length) return null;

    const params = {
      eq: { gender },
      in: { metrics },
    };

    const url = metricDistributionPercentilesUrl('teams', params);
    const data: MetricDistributionPercentiles[] = await fetch(url);
    return data;
  };

  return { queryKey, queryFn };
});

export const teamMetricDistributionPercentilesAtom = atom(async get => {
  const rawPercentiles = await get(rawTeamMetricDistributionPercentilesAtom);
  return formatMetricDistributions(rawPercentiles);
});

const rawTeamMetricDistributionBucketsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['teamMetricDistributionBuckets', get(unwrappedGenderAtom), get(metricsAtom)] as const;
  const queryFn = async ({ queryKey: [, gender, metrics] }: { queryKey: typeof queryKey }) => {
    if (!gender || !metrics.length) return null;

    const params = {
      eq: { gender },
      in: { metrics },
    };
    const url = metricDistributionBucketsUrl('teams', params);

    const data: MetricDistributionBucket[] = await fetch(url);

    return data;
  };

  return { queryKey, queryFn };
});

const teamMetricDistributionBucketsAtom = atom(async get =>
  formatMetricDistributionBuckets(await get(rawTeamMetricDistributionBucketsAtom)),
);

/** Fetch percentiles and buckets for selected metrics in parallel. This eliminates sequential suspension points. */
export const teamMetricPercentilesAndBucketsAtom = atom(async get => {
  const [percentiles, buckets] = await Promise.all([
    get(teamMetricDistributionPercentilesAtom),
    get(teamMetricDistributionBucketsAtom),
  ]);

  return { percentiles, buckets };
});
